import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext, VirtualTeacherContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Play, Clock, Star, User, ChevronRight, FileText, Users, Filter, Search, GraduationCap, Sparkles, Eye, Video } from 'lucide-react';
import { useOwnedContent } from '../hooks/useOwnedContent';
import { BoughtBadge } from '../components/BoughtBadge';
import { Checkbox } from '../components/ui/checkbox';

function AllCourses() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isBought = useOwnedContent();
  const { enabled: vtEnabled } = useContext(VirtualTeacherContext);
  // Respect the admin toggle for ALL roles (including admin) — admins manage
  // the flag from Settings, they don't need to see the student-facing entry
  // points on the catalog when it's off.
  const showVT = vtEnabled;
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [virtualTeacherLessons, setVirtualTeacherLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Filter states
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasQuiz, setHasQuiz] = useState(false);
  const [hasExam, setHasExam] = useState(false);
  const [hasOneOnOne, setHasOneOnOne] = useState(false);

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography'];
  const levels = ['Form I', 'Form II', 'Form III', 'Form IV'];

  useEffect(() => {
    const token = localStorage.getItem('token');
    Promise.all([
      axios.get(`${API}/courses?limit=50`),
      axios.get(`${API}/teachers`),
      token
        ? axios.get(`${API}/ai-tutor/library`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { lessons: [] } }))
        : Promise.resolve({ data: { lessons: [] } }),
    ]).then(([coursesRes, teachersRes, libRes]) => {
      setCourses(coursesRes.data);
      setTeachers(teachersRes.data);
      setVirtualTeacherLessons(libRes.data?.lessons || []);
      setLoading(false);
    });
  }, []);

  const toggleFilter = (value, filterArray, setFilterArray) => {
    if (filterArray.includes(value)) {
      setFilterArray(filterArray.filter(item => item !== value));
    } else {
      setFilterArray([...filterArray, value]);
    }
  };

  const filteredCourses = courses.filter(course => {
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!course.title?.toLowerCase().includes(q) && !course.description?.toLowerCase().includes(q) && !course.teacher_name?.toLowerCase().includes(q)) return false;
    }
    // Subject filter
    if (selectedSubjects.length > 0 && !selectedSubjects.includes(course.subject)) return false;
    
    // Level filter
    if (selectedLevels.length > 0 && !selectedLevels.includes(course.level)) return false;
    
    // Teacher filter
    if (selectedTeachers.length > 0 && !selectedTeachers.includes(course.teacher_id)) return false;
    
    // Price filter
    if (course.price < priceRange[0] || course.price > priceRange[1]) return false;
    
    // Quiz filter
    if (hasQuiz && !course.has_quiz) return false;
    
    // Exam filter
    if (hasExam && !course.has_exam) return false;
    
    // One-on-One filter
    if (hasOneOnOne && !course.includes_one_on_one) return false;
    
    return true;
  });

  const clearFilters = () => {
    setSelectedSubjects([]);
    setSelectedLevels([]);
    setSelectedTeachers([]);
    setPriceRange([0, 50000]);
    setHasQuiz(false);
    setHasExam(false);
    setHasOneOnOne(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-teal-600">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-semibold">All Classes on Demand</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">

        {/* Virtual Teacher · Classes on Demand */}
        {showVT && (
        <div
          className="mb-8 rounded-2xl p-5 md:p-6 border border-violet-200/70 shadow-sm"
          style={{
            background:
              'linear-gradient(135deg, rgba(245, 243, 255, 0.95), rgba(252, 231, 243, 0.85))',
          }}
          data-testid="virtual-teacher-section"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-300/40 shrink-0">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-violet-700 font-bold">
                  Classes on Demand
                </div>
                <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight">
                  Virtual Teacher · Mwalimu Asha
                </h2>
                <p className="text-xs text-slate-600 mt-0.5">
                  NECTA lessons taught on-demand by an AI avatar — pick a topic or browse the library.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/tutor')}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5 shrink-0"
              data-testid="virtual-teacher-cta"
            >
              <Sparkles className="w-3.5 h-3.5" /> Teach me a topic
            </Button>
          </div>

          {virtualTeacherLessons.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
              {virtualTeacherLessons.slice(0, 12).map((l) => (
                <button
                  key={l.id}
                  onClick={() => navigate(`/tutor/${l.id}`)}
                  className="snap-start text-left shrink-0 w-[230px] bg-white rounded-xl border border-violet-100 hover:border-violet-400 hover:shadow-md transition p-3"
                  data-testid={`vt-lesson-${l.id}`}
                >
                  <div className="aspect-video rounded-lg bg-gradient-to-br from-violet-100 to-fuchsia-100 mb-2 overflow-hidden flex items-center justify-center">
                    {l.thumbnail_url ? (
                      <img src={l.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Play className="w-7 h-7 text-violet-400" />
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-violet-600 font-semibold">
                    {l.subject} · {l.level}
                  </div>
                  <div className="font-semibold text-sm text-slate-900 line-clamp-2 leading-snug">
                    {l.topic}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-1">
                    <GraduationCap className="w-3 h-3 text-violet-500" />
                    {l?.persona?.name || 'Mwalimu Asha'}
                    <span className="text-slate-400">· Virtual</span>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                    <Eye className="w-3 h-3" /> {l.view_count || 0}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white/70 rounded-xl px-4 py-3 text-xs text-slate-600">
              Library is warming up — new lessons will appear here as the Virtual Teacher finishes them. Click <span className="font-semibold text-violet-700">Teach me a topic</span> to request your own.
            </div>
          )}
        </div>
        )}

        {/* Search Bar */}
        <div className="mb-6 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search courses by title, description, or teacher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            data-testid="search-courses-input"
          />
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-4 flex items-center justify-between">
          <Button variant="outline" onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="gap-2 border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="mobile-filter-toggle">
            <Filter className="w-4 h-4" /> Filters
            {(selectedSubjects.length + selectedLevels.length + selectedTeachers.length) > 0 && (
              <span className="bg-teal-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {selectedSubjects.length + selectedLevels.length + selectedTeachers.length}
              </span>
            )}
          </Button>
          <span className="text-sm text-slate-500">{filteredCourses.length} results</span>
        </div>

        {/* Mobile Filters Panel */}
        {showMobileFilters && (
          <div className="lg:hidden bg-white rounded-xl border border-slate-200 p-4 mb-6" data-testid="mobile-filters-panel">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Subject</label>
                <select onChange={(e) => e.target.value ? setSelectedSubjects([e.target.value]) : setSelectedSubjects([])} value={selectedSubjects[0] || ''}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  <option value="">All Subjects</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Level</label>
                <select onChange={(e) => e.target.value ? setSelectedLevels([e.target.value]) : setSelectedLevels([])} value={selectedLevels[0] || ''}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  <option value="">All Levels</option>
                  {levels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={hasQuiz} onCheckedChange={setHasQuiz} /> Quiz
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={hasExam} onCheckedChange={setHasExam} /> Exam
              </label>
              <Button variant="ghost" size="sm" onClick={() => { clearFilters(); setShowMobileFilters(false); }} className="text-xs ml-auto">Clear</Button>
            </div>
          </div>
        )}

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside className="w-72 flex-shrink-0 hidden lg:block" data-testid="filters-sidebar">
            <div className="bg-white rounded-xl p-6 border border-slate-200 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filters
                </h3>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">Clear All</Button>
              </div>

              {/* Subject Filter */}
              <div className="mb-6 pb-6 border-b border-slate-200">
                <h4 className="font-bold text-sm mb-3">Subject</h4>
                <div className="space-y-2">
                  {subjects.map(subject => (
                    <label key={subject} className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                      <Checkbox
                        checked={selectedSubjects.includes(subject)}
                        onCheckedChange={() => toggleFilter(subject, selectedSubjects, setSelectedSubjects)}
                      />
                      <span className="text-sm">{subject}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Level Filter */}
              <div className="mb-6 pb-6 border-b border-slate-200">
                <h4 className="font-bold text-sm mb-3">Level</h4>
                <div className="space-y-2">
                  {levels.map(level => (
                    <label key={level} className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                      <Checkbox
                        checked={selectedLevels.includes(level)}
                        onCheckedChange={() => toggleFilter(level, selectedLevels, setSelectedLevels)}
                      />
                      <span className="text-sm">{level}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Teacher Filter */}
              <div className="mb-6 pb-6 border-b border-slate-200">
                <h4 className="font-bold text-sm mb-3">Teacher</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {teachers.map(teacher => (
                    <label key={teacher.id} className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                      <Checkbox
                        checked={selectedTeachers.includes(teacher.id)}
                        onCheckedChange={() => toggleFilter(teacher.id, selectedTeachers, setSelectedTeachers)}
                      />
                      <span className="text-sm">{teacher.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Features Filter */}
              <div className="mb-6">
                <h4 className="font-bold text-sm mb-3">Includes</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                    <Checkbox checked={hasQuiz} onCheckedChange={setHasQuiz} />
                    <span className="text-sm">Quiz</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                    <Checkbox checked={hasExam} onCheckedChange={setHasExam} />
                    <span className="text-sm">Exam</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer hover:text-teal-600">
                    <Checkbox checked={hasOneOnOne} onCheckedChange={setHasOneOnOne} />
                    <span className="text-sm">Free 1-on-1 Session</span>
                  </label>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <div className="mb-6">
              <h1 className="text-3xl md:text-4xl font-black mb-2">All Classes on Demand</h1>
              <p className="text-slate-600">Showing {filteredCourses.length} of {courses.length} courses</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {filteredCourses.map(course => (
                <Link 
                  key={course.id} 
                  to={`/courses/${course.id}`} 
                  className="group bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-slate-200 hover:shadow-2xl hover:shadow-slate-200/60 transition-all duration-300 hover:-translate-y-1"
                  data-testid={`course-card-${course.id}`}
                >
                  {/* Mobile compact card */}
                  <div className="sm:hidden">
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" loading="lazy" />
                      {course.price === 0 && <span className="absolute top-1 left-1 bg-green-500 text-white text-[7px] px-1 py-0.5 rounded font-bold">FREE</span>}
                      {course.recordings_count > 0 && (
                        <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 bg-teal-600/95 text-white text-[7px] px-1 py-0.5 rounded font-bold" data-testid={`course-recordings-badge-${course.id}`}>
                          <Video className="w-2 h-2" />{course.recordings_count}
                        </span>
                      )}
                    </div>
                    <div className="p-1.5">
                      <h3 className="text-[11px] font-semibold line-clamp-2 text-slate-800 leading-tight">{course.title}</h3>
                      <p className="text-[9px] text-slate-500 mt-0.5 line-clamp-1">{course.teacher_name}</p>
                    </div>
                  </div>

                  {/* Desktop detailed card */}
                  <div className="hidden sm:block">
                    <div className="relative overflow-hidden h-48">
                      <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent"></div>
                      <div className="absolute top-3 left-3 bg-amber-500 text-black px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        Best Seller
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-xl">
                          <Play className="w-6 h-6 text-teal-600 ml-1" />
                        </div>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">{course.level}</span>
                        <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-xs font-bold">{course.subject}</span>
                        {course.recordings_count > 0 && (
                          <span className="inline-flex items-center gap-1 bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold" data-testid={`course-recordings-badge-${course.id}`}>
                            <Video className="w-3 h-3" />
                            {course.recordings_count} {course.recordings_count === 1 ? 'recording' : 'recordings'}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">{course.title}</h3>
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2 leading-relaxed">{course.topic_explanation}</p>
                      <p className="text-sm text-slate-600 mb-3 flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />by {course.teacher_name}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-1"><Clock className="w-4 h-4" />{course.duration}min</div>
                        <div className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-500 fill-amber-500" /><span className="font-semibold text-slate-700">{course.rating}</span></div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {course.has_quiz && <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs font-semibold"><FileText className="w-3 h-3" />Quiz</div>}
                        {course.has_exam && <div className="flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-1 rounded-md text-xs font-semibold"><FileText className="w-3 h-3" />Exam</div>}
                        {course.includes_one_on_one && <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-md text-xs font-semibold"><Users className="w-3 h-3" />Free 1-on-1</div>}
                      </div>
                      <div className="flex items-center justify-between">
                        {isBought('course', course) ? <BoughtBadge size="md" testId={`course-bought-${course.id}`} /> : (
                          <span className="text-2xl font-black text-teal-600">{(course.price || 0) === 0 ? 'Free' : `TSh ${(course.price || 0).toLocaleString()}`}</span>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredCourses.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500">No courses match your filters. Try adjusting your selection.</p>
                <Button onClick={clearFilters} className="mt-4">Clear Filters</Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default AllCourses;